import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { globalProductIdManagerToken } from "@/tokens";
import { ReviewsListWidgetDefinition } from "@apps/reviews/definitions";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const REVIEW_PRODUCT_ID = "gw-product-id";
const ADD_REVIEW_BUTTON_ENABLED = "gw-add-review-button-enabled";
const REVIEWS_PER_PAGE = "gw-reviews-per-page";

const REVIEWS_PER_PAGE_DEFAULT = 5;

class ReviewsListWidgetInjector {
    private moi: MutationObserverInjector;
    private ioi: IntersectionObserverInjector;
    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager
    ) {
        this.moi = new MutationObserverInjector(MutationObserver, {
            mutationTarget: document.body,
            targetSelector: this.placeholderSelector,
        });
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("ReviewsListWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            this.logger?.debug("ReviewsListWidgetInjector found placeholder", {
                placeholder,
            });

            const productIdFromDataAttr =
                parseIntDataAttr(placeholder, REVIEW_PRODUCT_ID) ||
                parseIntFromGidDataAttr(placeholder, REVIEW_PRODUCT_ID);

            if (productIdFromDataAttr === null) {
                this.logger.error(
                    `ReviewsListWidget placeholder does not have data-${REVIEW_PRODUCT_ID} attribute.`
                );
            }

            const addReviewButtonEnabledDataAttr = parseOptionalBooleanDataAttr(
                placeholder,
                ADD_REVIEW_BUTTON_ENABLED
            );

            const reviewsPerPageFromDataAttr =
                parseIntDataAttr(placeholder, REVIEWS_PER_PAGE) ||
                REVIEWS_PER_PAGE_DEFAULT;

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            void (async () => {
                const ReviewsListWidget =
                    await this.dynamicImportManager.importDefault(
                        ReviewsListWidgetDefinition,
                        true
                    );

                const widget = new ReviewsListWidget(
                    {
                        target: placeholder,
                        reviewsPerPage: reviewsPerPageFromDataAttr,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    this.logger,
                    productIdManager,
                    {
                        showAddReviewButton:
                            addReviewButtonEnabledDataAttr ?? true,
                    }
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        REVIEWS_WIDGET_NAMES.REVIEWS_LIST_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        REVIEWS_WIDGET_NAMES.REVIEWS_LIST_WIDGET
                    )
                );
            })();
        });
        this.ioi.start();
    }
}

export default ReviewsListWidgetInjector;
